import type { TFieldTrackDay, TFieldTrackDevice } from "@src/services/fieldsService";
import type { TTrackGroup } from "@screens/FieldsScreen/fieldTracksLayer";

/*
 * The pure half of the Fields tracks day cache, shared by FieldsScreenVM and
 * CompareVM — both used to inline these loops. The stores themselves (the
 * cache Map, batching, the bug-118 rebuild/append split) stay in each VM.
 */

/**
 * Split a batch response into per-day slices so each day can be cached
 * independently of the batch it happened to arrive in. Every requested day
 * gets an entry: a day with no points caches as empty, so it is never
 * re-requested. Points outside the requested days are dropped.
 */
export function splitBatchByDay(
  batchDays: string[],
  devices: TFieldTrackDevice[]
): Map<string, TFieldTrackDevice[]> {
  const wanted = new Set(batchDays);
  const perDay = new Map<string, Map<string, TFieldTrackDevice>>();
  for (const day of batchDays) perDay.set(day, new Map());

  for (const device of devices) {
    for (const row of device.tracks ?? []) {
      const day = String(row.time).slice(0, 10);
      if (!wanted.has(day)) continue;
      const byDevice = perDay.get(day)!;
      let entry = byDevice.get(device.deviceId);
      if (!entry) {
        entry = { ...device, tracks: [] };
        byDevice.set(device.deviceId, entry);
      }
      entry.tracks.push(row);
    }
  }
  const out = new Map<string, TFieldTrackDevice[]>();
  for (const [day, byDevice] of perDay) out.set(day, [...byDevice.values()]);
  return out;
}

/**
 * The UTC days whose day-summary rows differ between two summaries: a
 * machine's point count or first / last fix moved, a machine appeared or left
 * the day, or the day itself appeared or went. Sorted.
 *
 * The cache treated every day before UTC yesterday as final, but a John Deere
 * import lands in batches every few hours and a re-import rewrites the
 * operation's whole window, so any day of the season can change — and the
 * machine picker listed the new data while the map drew the old points.
 * The summary rows are what the backend rescans when that happens, so a row
 * that changed is a cached day to drop. A row without firstAt/lastAt (an
 * older backend) is compared on its point count alone.
 */
export function changedTrackDays(prev: TFieldTrackDay[], next: TFieldTrackDay[]): string[] {
  const signatures = (rows: TFieldTrackDay[]): Map<string, string> => {
    const perDay = new Map<string, string[]>();
    for (const r of rows) {
      const parts = perDay.get(r.day) ?? [];
      parts.push(`${r.deviceId}|${r.points}|${r.firstAt ?? ""}|${r.lastAt ?? ""}`);
      perDay.set(r.day, parts);
    }
    return new Map([...perDay].map(([day, parts]) => [day, parts.sort().join(",")]));
  };
  const before = signatures(prev);
  const after = signatures(next);
  const changed = new Set<string>();
  for (const [day, sig] of before) if (after.get(day) !== sig) changed.add(day);
  for (const day of after.keys()) if (!before.has(day)) changed.add(day);
  return [...changed].sort();
}

/**
 * The (day, machine) groups to draw, from whatever is cached, in `dayList`
 * order, keeping only visible machines (and only `onlyDays` when given).
 *
 * Grouping by day is what lets a feature be coloured by its date, and it
 * removes the cross-day sort that used to be needed: each group's points come
 * from one day's response and are already chronological.
 */
export function assembleTrackGroups(
  dayList: string[],
  cache: Map<string, TFieldTrackDevice[]>,
  visibleDeviceIds: Iterable<string>,
  onlyDays?: string[]
): TTrackGroup[] {
  const visible = new Set(visibleDeviceIds);
  const groups: TTrackGroup[] = [];
  const include = onlyDays ? new Set(onlyDays) : null;
  for (const day of dayList) {
    if (include && !include.has(day)) continue;
    for (const device of cache.get(day) ?? []) {
      if (!visible.has(device.deviceId)) continue;
      groups.push({ day, device });
    }
  }
  return groups;
}

/*
 * Local days. The backend buckets, fetches and caches by UTC calendar day;
 * people read days in their own zone, like the Tracks screen. So everything
 * fetched stays keyed by UTC day and only what is shown — the day list, Date
 * colours, the focused day — is a local (browser) day. A UTC day overlaps at
 * most two local days.
 */

/** "YYYY-MM-DD" of a unix time in the browser's zone. */
export function localDayKey(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${d.getFullYear()}-${m < 10 ? "0" : ""}${m}-${day < 10 ? "0" : ""}${day}`;
}

const rowLocalDay = (time: unknown): string | null => {
  const t = Date.parse(String(time));
  return Number.isFinite(t) ? localDayKey(t / 1000) : null;
};

/**
 * Local day → the UTC days holding its points, both sorted.
 *
 * A summary row's first and last fix are real fixes and its points are one
 * contiguous span, so the local days of those two ends are exactly the local
 * days the row has work on. A row without them (a backend from before
 * firstAt/lastAt) is taken at its UTC date.
 */
export function localDayIndex(rows: TFieldTrackDay[]): Map<string, string[]> {
  const index = new Map<string, Set<string>>();
  for (const row of rows) {
    const ends =
      row.firstAt && row.lastAt
        ? [rowLocalDay(row.firstAt), rowLocalDay(row.lastAt)]
        : [row.day];
    for (const local of ends) {
      if (!local) continue;
      if (!index.has(local)) index.set(local, new Set());
      index.get(local)!.add(row.day);
    }
  }
  return new Map(
    [...index.keys()].sort().map((local) => [local, [...index.get(local)!].sort()])
  );
}

/**
 * Each machine's points on one local day, gathered from the cached UTC days
 * that overlap it. Chronological: the UTC days come sorted and each day's rows
 * already are. Machines with nothing that day are left out.
 */
export function devicesForLocalDay(
  localDay: string,
  utcDays: string[],
  cache: Map<string, TFieldTrackDevice[]>
): TFieldTrackDevice[] {
  const byDevice = new Map<string, TFieldTrackDevice>();
  for (const utc of utcDays) {
    for (const device of cache.get(utc) ?? []) {
      let entry = byDevice.get(device.deviceId);
      if (!entry) {
        entry = { ...device, tracks: [] };
        byDevice.set(device.deviceId, entry);
      }
      for (const row of device.tracks ?? []) {
        if (rowLocalDay(row.time) === localDay) entry.tracks.push(row);
      }
    }
  }
  return [...byDevice.values()].filter((d) => d.tracks.length > 0);
}
